import { useMemo, useState } from "react";
import { useMutation, useQueryClient } from "@tanstack/react-query";
import { Loader2 } from "lucide-react";
import { Button } from "@/components/ui/button";
import { useToast } from "@/hooks/use-toast";
import { whatsappApi } from "@/services/whatsapp-api";
import {
  CATEGORIA_LABELS,
  type WhatsAppItem,
  type WhatsAppItemCategoria,
  type WhatsAppMotivoDescarte,
} from "@/types/whatsapp";
import { WhatsAppAssociarDialog } from "./WhatsAppAssociarDialog";
import {
  WhatsAppContatoCard,
  type WhatsAppContatoGroup,
} from "./WhatsAppContatoCard";
import { WhatsAppIgnorarDialog } from "./WhatsAppIgnorarDialog";
import { WhatsAppTranscriptDialog } from "./WhatsAppTranscriptDialog";
import { toUserError } from "@/lib/api-error";

const CATEGORIAS = Object.keys(CATEGORIA_LABELS) as WhatsAppItemCategoria[];
const ORDEM_CATEGORIA: Record<WhatsAppItemCategoria, number> = {
  disponibilidade: 0,
  negociacao: 1,
  sentimento: 2,
};

/** Agrupa itens pelo contato: freelancer > telefone > conversa. */
export function groupByContato(items: WhatsAppItem[]): WhatsAppContatoGroup[] {
  const groups = new Map<string, WhatsAppContatoGroup>();
  for (const item of items) {
    const key =
      item.freelancer_id ?? item.telefone ?? `conv:${item.conversation_id}`;
    let group = groups.get(key);
    if (!group) {
      group = {
        key,
        freelancerId: item.freelancer_id,
        freelancerNome: item.freelancer_nome,
        contactName: item.contact_name,
        telefone: item.telefone,
        items: [],
      };
      groups.set(key, group);
    }
    group.items.push(item);
  }
  for (const group of groups.values()) {
    group.items.sort(
      (a, b) =>
        ORDEM_CATEGORIA[a.categoria] - ORDEM_CATEGORIA[b.categoria] ||
        a.observed_at.localeCompare(b.observed_at),
    );
  }
  // Mais itens primeiro: quem tem várias mudanças rende mais por revisão.
  return [...groups.values()].sort((a, b) => b.items.length - a.items.length);
}

interface WhatsAppFilaRevisaoProps {
  items: WhatsAppItem[];
  /** Mensagem quando não há nada (varia entre fila vazia e filtro sem match). */
  vazio: string;
  /** Filtro por categoria em chips; desligado no histórico (que tem os seus). */
  comFiltroCategoria?: boolean;
}

export function WhatsAppFilaRevisao({
  items,
  vazio,
  comFiltroCategoria = true,
}: WhatsAppFilaRevisaoProps) {
  const { toast } = useToast();
  const queryClient = useQueryClient();

  const [categoriaFiltro, setCategoriaFiltro] = useState<
    WhatsAppItemCategoria | "todas"
  >("todas");
  const [transcriptItem, setTranscriptItem] = useState<WhatsAppItem | null>(null);
  const [associarItem, setAssociarItem] = useState<WhatsAppItem | null>(null);
  const [ignorarItem, setIgnorarItem] = useState<WhatsAppItem | null>(null);
  const [aprovandoId, setAprovandoId] = useState<string | null>(null);

  const invalidate = () => {
    queryClient.invalidateQueries({ queryKey: ["whatsapp-items"] });
    queryClient.invalidateQueries({ queryKey: ["whatsapp-resumo"] });
    queryClient.invalidateQueries({ queryKey: ["whatsapp-runs"] });
  };

  const aprovar = useMutation({
    mutationFn: ({
      item,
      posicaoId,
      payloadCorrigido,
    }: {
      item: WhatsAppItem;
      posicaoId?: string;
      payloadCorrigido?: Record<string, unknown>;
    }) =>
      whatsappApi.aprovar(item.id, {
        ...(posicaoId ? { posicao_id: posicaoId } : {}),
        ...(payloadCorrigido ? { payload_corrigido: payloadCorrigido } : {}),
      }),
    onMutate: ({ item }) => setAprovandoId(item.id),
    onSettled: () => setAprovandoId(null),
    onSuccess: (_resultado, variaveis) => {
      toast({
        title: variaveis.payloadCorrigido ? "Aprovado com correção" : "Item aprovado",
        description: "O efeito foi aplicado.",
      });
      invalidate();
    },
    onError: (error: Error) => {
      toast({
        title: "Erro ao aprovar",
        description: toUserError(error).description,
        variant: "destructive",
      });
    },
  });

  const rejeitar = useMutation({
    mutationFn: ({
      item,
      motivo,
      nota,
    }: {
      item: WhatsAppItem;
      motivo?: WhatsAppMotivoDescarte;
      nota?: string;
    }) => whatsappApi.rejeitar(item.id, { motivo, nota }),
    onSuccess: () => {
      toast({ title: "Item descartado" });
      invalidate();
    },
    onError: (error: Error) => {
      toast({
        title: "Erro ao descartar",
        description: toUserError(error).description,
        variant: "destructive",
      });
    },
  });

  const aprovarLote = useMutation({
    mutationFn: (itemIds: string[]) => whatsappApi.aprovarLote(itemIds),
    onSuccess: (result) => {
      if (result.falhas.length === 0) {
        toast({
          title: "Aprovados",
          description: `${result.aprovados.length} itens aprovados.`,
        });
      } else {
        const detalhe = result.falhas
          .slice(0, 5)
          .map((falha) => `• ${falha.reason}`)
          .join("\n");
        toast({
          variant: result.aprovados.length > 0 ? "default" : "destructive",
          title: `${result.aprovados.length} aprovados · ${result.falhas.length} falharam`,
          description: detalhe,
        });
      }
      invalidate();
    },
    onError: (error: Error) => {
      toast({
        title: "Erro na aprovação em lote",
        description: toUserError(error).description,
        variant: "destructive",
      });
    },
  });

  const pendentesPorCategoria = useMemo(() => {
    const counts: Record<string, number> = {};
    for (const item of items) {
      counts[item.categoria] = (counts[item.categoria] ?? 0) + 1;
    }
    return counts;
  }, [items]);

  const groups = useMemo(
    () =>
      groupByContato(
        categoriaFiltro === "todas"
          ? items
          : items.filter((item) => item.categoria === categoriaFiltro),
      ),
    [items, categoriaFiltro],
  );

  return (
    <div className="space-y-4">
      {comFiltroCategoria && items.length > 0 && (
        <div className="flex flex-wrap items-center gap-2">
          <Button
            variant={categoriaFiltro === "todas" ? "default" : "outline"}
            size="sm"
            onClick={() => setCategoriaFiltro("todas")}
          >
            Todas ({items.length})
          </Button>
          {CATEGORIAS.map((categoria) => (
            <Button
              key={categoria}
              variant={categoriaFiltro === categoria ? "default" : "outline"}
              size="sm"
              onClick={() => setCategoriaFiltro(categoria)}
              disabled={!pendentesPorCategoria[categoria]}
            >
              {CATEGORIA_LABELS[categoria]} ({pendentesPorCategoria[categoria] ?? 0})
            </Button>
          ))}
        </div>
      )}

      {groups.length === 0 ? (
        <div className="py-10 text-center text-sm text-muted-foreground">{vazio}</div>
      ) : (
        <div className="space-y-4">
          {groups.map((group) => (
            <WhatsAppContatoCard
              key={group.key}
              group={group}
              aprovandoId={aprovandoId}
              aprovandoLote={aprovarLote.isPending}
              onAprovar={(item, posicaoId, payloadCorrigido) =>
                aprovar.mutate({ item, posicaoId, payloadCorrigido })
              }
              onRejeitar={(item, motivo, nota) =>
                rejeitar.mutate({ item, motivo, nota })
              }
              onAprovarTodos={(groupItems) =>
                aprovarLote.mutate(groupItems.map((item) => item.id))
              }
              onAssociar={(item) => setAssociarItem(item)}
              onIgnorar={(item) => setIgnorarItem(item)}
              onVerConversa={(item) => setTranscriptItem(item)}
            />
          ))}
        </div>
      )}

      {aprovarLote.isPending && (
        <div className="flex items-center justify-center gap-2 text-sm text-muted-foreground">
          <Loader2 className="h-4 w-4 animate-spin" />
          Aplicando aprovações...
        </div>
      )}

      <WhatsAppTranscriptDialog
        conversationId={transcriptItem?.conversation_id ?? null}
        highlightIds={transcriptItem?.message_ids ?? []}
        onClose={() => setTranscriptItem(null)}
      />
      <WhatsAppAssociarDialog
        item={associarItem}
        onClose={() => setAssociarItem(null)}
      />
      <WhatsAppIgnorarDialog
        item={ignorarItem}
        onClose={() => setIgnorarItem(null)}
      />
    </div>
  );
}
